import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  SBadge,
  SButton,
  SIcon,
  SModal,
  SRadioGroup,
} from '@chargebee/sting-react';
import { getPlayTypeFromRoute } from '@bbkAdminComponents/unified-plays/utils/play-type-utils';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import {
  DEFAULT_BRAND_ID,
  useGetSelectedBrand,
} from '@bbkAdminRedux/rtkq/brands.slice';
import { PlayPresentation, PlayType } from '@bbkAdminRedux/rtkq/play.slice';
import { encodeObjectForQuery } from '@bbkAdminRedux/rtkq/rtkq-utils';
import { AuthorizedPaths } from '@bbkAdminRouter/router-paths';
import { useUrl } from '@bbkAdminUtils/nav';
import { useHistory } from '@bbkShared/react-router';

type PresentationTypeOption = {
  type: PlayPresentation;
  icon: React.ReactNode;
  title: string;
  description: string;
};

const PRESENTATION_TYPE_OPTIONS: PresentationTypeOption[] = [
  {
    type: PlayPresentation.MODAL,
    icon: <SIcon name="square-stop" size={16} />,
    title: 'Modal',
    description: 'Full-screen overlay that captures user attention.',
  },
  {
    type: PlayPresentation.POP_UP,
    icon: <SIcon name="vote" size={16} />,
    title: 'Pop-up',
    description: 'Small contextual message that appears over content.',
  },
  {
    type: PlayPresentation.BANNER,
    icon: <SIcon name="panel-top" size={16} />,
    title: 'Banner',
    description: 'Horizontal bar at the top or bottom of the screen.',
  },
];

enum PlayTypeOption {
  PRICING_TABLE = 'pricing-table',
  CANCEL_PAGE = 'cancel-page',
  IN_APP = 'in-app',
}

type AdditionalNavigationState = {
  riskCategory?: string;
  name?: string;
  returnTab?: string;
  returnUrl?: string;
  [key: string]: unknown;
};

type CreatePlayModalProps = {
  isOpen: boolean;
  onClose: (open: boolean) => void;
  routePlayType: string | undefined;
  additionalState?: AdditionalNavigationState;
};

export const CreatePlayModal: React.FC<CreatePlayModalProps> = ({
  isOpen,
  onClose,
  routePlayType,
  additionalState,
}) => {
  const [selectedPlayType, setSelectedPlayType] =
    useState<PlayTypeOption | null>(null);
  const [selectedPresentationType, setSelectedPresentationType] =
    useState<PlayPresentation | null>(null);
  const history = useHistory();
  const createUrl = useUrl();

  const appKey = useSelector(selectAppId);
  const playType = getPlayTypeFromRoute(routePlayType);
  const isRetention = playType === PlayType.RETENTION;
  const brandId = useGetSelectedBrand(appKey) ?? DEFAULT_BRAND_ID;

  const availablePlayTypeOptions = useMemo(() => {
    if (isRetention) {
      return [PlayTypeOption.CANCEL_PAGE, PlayTypeOption.IN_APP];
    } else {
      return [PlayTypeOption.PRICING_TABLE, PlayTypeOption.IN_APP];
    }
  }, [isRetention]);

  const handleProceed = useCallback(() => {
    const getNavigationUrl = () => {
      const baseUrl = createUrl(AuthorizedPaths.unifiedPlaysNew);
      if (additionalState?.returnUrl) {
        return (
          baseUrl +
          encodeObjectForQuery({ returnUrl: additionalState.returnUrl })
        );
      }
      return baseUrl;
    };

    const getNavigationState = (presentationType: PlayPresentation) => ({
      playType: getPlayTypeFromRoute(routePlayType),
      presentationType,
      brandId,
      ...additionalState,
    });

    if (selectedPlayType === PlayTypeOption.PRICING_TABLE) {
      history.push(
        getNavigationUrl(),
        getNavigationState(PlayPresentation.PRICING_PAGE)
      );
    } else if (selectedPlayType === PlayTypeOption.CANCEL_PAGE) {
      history.push(
        getNavigationUrl(),
        getNavigationState(PlayPresentation.CANCEL_PAGE)
      );
    } else if (
      selectedPlayType === PlayTypeOption.IN_APP &&
      selectedPresentationType
    ) {
      history.push(
        getNavigationUrl(),
        getNavigationState(selectedPresentationType)
      );
    }
    onClose(false);
  }, [
    selectedPlayType,
    selectedPresentationType,
    history,
    createUrl,
    routePlayType,
    onClose,
    brandId,
    additionalState,
  ]);

  const canProceed =
    selectedPlayType === PlayTypeOption.PRICING_TABLE ||
    selectedPlayType === PlayTypeOption.CANCEL_PAGE ||
    (selectedPlayType === PlayTypeOption.IN_APP &&
      selectedPresentationType !== null);

  return (
    <SModal open={isOpen} onOpenChange={onClose}>
      <SModal.Content size="small" padding="large">
        <SModal.Header showCloseButton className="tw-pb-md">
          <SModal.Title className="!tw-text-2xl tw-font-semibold tw-text-neutral-900">
            Choose{' '}
            {playType === PlayType.RETENTION
              ? 'retention'
              : playType === PlayType.UPSELL
                ? 'expansion'
                : 'acquisition'}{' '}
            play type
          </SModal.Title>
          <SModal.Description className="tw-text-base tw-text-neutral-600">
            {playType === PlayType.RETENTION
              ? 'Keep customers engaged and prevent churn'
              : playType === PlayType.UPSELL
                ? 'Grow revenue from existing customers'
                : 'Convert prospects into paying customers.'}
          </SModal.Description>
        </SModal.Header>
        <SModal.Body className="tw-px-3xl tw-pb-3xl">
          <div className="tw-flex tw-flex-col tw-gap-md">
            <SRadioGroup
              value={selectedPlayType || ''}
              onValueChange={(value) => {
                setSelectedPlayType(value as PlayTypeOption);
                if (
                  value === PlayTypeOption.PRICING_TABLE ||
                  value === PlayTypeOption.CANCEL_PAGE
                ) {
                  setSelectedPresentationType(null);
                } else if (value === PlayTypeOption.IN_APP) {
                  setSelectedPresentationType(PlayPresentation.MODAL);
                }
              }}
              variant="default"
              orientation="vertical"
              fluid
              className="[&_.radio-option-label]:!tw-m-0"
            >
              {availablePlayTypeOptions.includes(
                PlayTypeOption.PRICING_TABLE
              ) && (
                <SRadioGroup.Item
                  value={PlayTypeOption.PRICING_TABLE}
                  label={
                    <SBadge
                      icon={<SIcon name="grid-3x2" />}
                      iconPosition="left"
                      mode="light"
                      size="large"
                      variant="neutral"
                      className="!tw-inline-flex !tw-items-center !tw-gap-2 tw-text-base [&>span]:tw-flex [&>span]:tw-items-center"
                    >
                      Pricing table
                    </SBadge>
                  }
                  description={
                    <span className="tw-text-sm">
                      Deploy personalized pricing tables with dynamic plan
                      purchase and upgrade options. Requires installing
                      Chargebee.js.
                    </span>
                  }
                />
              )}
              {availablePlayTypeOptions.includes(
                PlayTypeOption.CANCEL_PAGE
              ) && (
                <SRadioGroup.Item
                  value={PlayTypeOption.CANCEL_PAGE}
                  label={
                    <SBadge
                      icon={<SIcon name="user-x" />}
                      iconPosition="left"
                      mode="light"
                      size="large"
                      variant="neutral"
                      className="!tw-inline-flex !tw-items-center !tw-gap-2 tw-text-base [&>span]:tw-flex [&>span]:tw-items-center"
                    >
                      Cancel page
                    </SBadge>
                  }
                  description={
                    <span className="tw-text-sm">
                      Create personalized cancellation journeys and present
                      targeted retention offers to subscribers at the moment of
                      cancel. Requires installing Chargebee.js.{' '}
                    </span>
                  }
                />
              )}
              {availablePlayTypeOptions.includes(PlayTypeOption.IN_APP) && (
                <SRadioGroup.Item
                  value={PlayTypeOption.IN_APP}
                  label={
                    <SBadge
                      icon={<SIcon name="badge-percent" />}
                      iconPosition="left"
                      mode="light"
                      size="large"
                      variant="neutral"
                      className="!tw-inline-flex !tw-items-center !tw-gap-2 tw-text-base [&>span]:tw-flex [&>span]:tw-items-center"
                    >
                      In-app offer
                    </SBadge>
                  }
                  description={
                    <div>
                      <p className="tw-mb-sm tw-text-sm">
                        Show personalized contextual offers to your subscribers
                        using Chargebee.js or through our API for fully custom
                        experiences.
                      </p>
                      {selectedPlayType === PlayTypeOption.IN_APP && (
                        <div className="tw-mt-md tw-border-t tw-border-neutral-200 tw-pt-md">
                          <h4 className="tw-mb-sm tw-text-sm tw-font-semibold tw-text-neutral-900">
                            Choose in-app offer type:
                          </h4>
                          <SRadioGroup
                            value={selectedPresentationType || ''}
                            onValueChange={(value) =>
                              setSelectedPresentationType(
                                value as PlayPresentation
                              )
                            }
                            variant="default"
                            orientation="vertical"
                            fluid
                          >
                            {PRESENTATION_TYPE_OPTIONS.map((option) => (
                              <SRadioGroup.Item
                                key={option.type}
                                value={option.type}
                                label={
                                  <SBadge
                                    icon={option.icon}
                                    iconPosition="left"
                                    mode="light"
                                    size="large"
                                    variant="neutral"
                                    className="!tw-inline-flex !tw-items-center !tw-gap-2 tw-text-base [&>span]:tw-flex [&>span]:tw-items-center"
                                  >
                                    {option.title}
                                  </SBadge>
                                }
                                description={
                                  <span className="tw-text-sm">
                                    {option.description}
                                  </span>
                                }
                              />
                            ))}
                          </SRadioGroup>
                        </div>
                      )}
                    </div>
                  }
                />
              )}
            </SRadioGroup>
          </div>
        </SModal.Body>
        <div className="tw-border-t tw-border-neutral-200" />
        <SModal.Footer className="!tw-flex-row">
          <SModal.Close asChild>
            <SButton
              className="tw-flex tw-w-full tw-justify-center md:tw-w-auto"
              variant="neutral"
              size="large"
            >
              Discard
            </SButton>
          </SModal.Close>
          <SButton
            className="tw-flex tw-w-full tw-justify-center md:tw-w-auto"
            onClick={handleProceed}
            disabled={!canProceed}
            icon={<SIcon name="arrow-right" size={16} />}
            iconPosition="right"
            size="large"
          >
            Proceed
          </SButton>
        </SModal.Footer>
      </SModal.Content>
    </SModal>
  );
};
